import { useMemo } from 'react';
import type { EmotionCache } from '@emotion/react';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { bucketFetcher } from '@packages/cms-components';
import { getEndpoint } from '@packages/cms-components/src/utils/cmsFetcher';
import config, { getFullLocale } from '@packages/config';
import { headerDefaultFetcher, PageLayout } from '@packages/modules';
import {
  userAgentDeviceCacheKey,
  cached,
  ConfigProvider,
  CookieProvider,
  createEmotionCache,
  ErrorBoundary,
  execParallel,
  LanguageProvider,
  sendWebVitals,
  SessionProvider,
  usePersistentScrid,
} from '@packages/shared';
import type { GeoCountryCode, Language } from '@packages/config';
import { theme } from '@packages/themes';
import favicon from '@packages/themes/dist/favicon.ico';
import { Mouseflow, TrackingProvider } from '@packages/tracking';
import type { AppContext, AppProps, NextWebVitalsMetric } from 'next/app';
import App from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { parseCookies } from 'nookies';
import { SWRConfig } from 'swr';
import { Provider as JotaiProvider } from 'jotai';
import { getNavigationDataEndpoint } from '@packages/modules/src/Header/Navigation/getNavigationDataEndpoint';
import { getUspDataEndpoint } from '@packages/modules/src/Header/Usp';
import type { Device } from '@packages/themes/src/default';
import { UrqlProvider } from '@packages/gql';
import { PreviewHeaderContainer } from '@packages/cms-components/src/components/Preview/PreviewHead';
import { localizedPathnameCacheKey } from '@packages/config/src/default';
import messages from '../../locales/messages';
import { getGeoCountryCode } from '../util/getGeoCountryCode';
import Custom404 from './404';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface ShoppingAppProps extends AppProps<any> {
  emotionCache?: EmotionCache;
  device: Device;
  geoCountryCode?: GeoCountryCode;
  isBot: boolean;
  cookies: Record<string, string>;
  fallback: Record<string, any>;
}

const MyApp = ({
  Component,
  emotionCache = clientSideEmotionCache,
  pageProps,
  device,
  geoCountryCode,
  isBot,
  cookies,
  fallback,
}: ShoppingAppProps) => {
  const router = useRouter();
  const locale = getFullLocale(router.locale as Language);

  const { fallback: pageFallback, ...otherPageProps } = pageProps;

  const pageFallbackFinal = {
    ...pageFallback,
    // force reset of localizedPathname if not exist
    // used by LocaleSwitchSlot.tsx
    [localizedPathnameCacheKey]: pageFallback?.[localizedPathnameCacheKey] || null,
  };

  // theme construction is expensive, so we memoize it
  // on 6x CPU throttling this saves about 350ms per re-render
  const memoizedTheme = useMemo(() => theme(device), [device]);

  usePersistentScrid();

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <meta
          name="viewport"
          content="initial-scale=1, width=device-width, shrink-to-fit=no, maximum-scale=1"
        />
        <link rel="shortcut icon" href={favicon.src} />
      </Head>
      <CookieProvider cookies={cookies}>
        <ConfigProvider>
          <SessionProvider>
            <UrqlProvider pageProps={pageProps}>
              <LanguageProvider
                locale={locale}
                messages={messages[locale as keyof typeof messages]}
                defaultLocale={config.i18n.defaultLocale}
              >
                <TrackingProvider
                  tagmanagerId={config.tracking.tagmanagerId}
                  domain={config.tracking.tagmanagerDomain}
                  isBot={isBot}
                >
                  <ThemeProvider theme={memoizedTheme}>
                    {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                    <CssBaseline />
                    <SWRConfig
                      value={{
                        fallback:
                          'discardSsrCache' in router.query
                            ? {}
                            : { ...fallback, ...pageFallbackFinal },
                        dedupingInterval: 3000,
                      }}
                    >
                      <JotaiProvider>
                        {pageProps.isPreviewPage && <PreviewHeaderContainer />}
                        <PageLayout geoCountryCode={geoCountryCode}>
                          {/* TODO get useful fallback from UI/UX */}
                          <ErrorBoundary fallback={<Custom404 />}>
                            <Component {...otherPageProps} />
                            {/* adds mouseflow tracking script */}
                            <Mouseflow />
                          </ErrorBoundary>
                        </PageLayout>
                      </JotaiProvider>
                    </SWRConfig>
                  </ThemeProvider>
                </TrackingProvider>
              </LanguageProvider>
            </UrqlProvider>
          </SessionProvider>
        </ConfigProvider>
      </CookieProvider>
    </CacheProvider>
  );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const { ctx } = appContext;
  let fallback: Record<string, any> | undefined;

  const detectedDevice: Device = ctx.req?.headers['x-ua-device'] as Device;
  const isBot: boolean = ctx.req?.headers['x-ua-bot'] !== undefined;
  const cookies = parseCookies(ctx);
  const geoCountryCode: GeoCountryCode = getGeoCountryCode(ctx);

  if (ctx.req) {
    const { clientId, footer } = config;
    const locale = ctx.locale || ctx.defaultLocale || 'de';
    const localeForCacheKey = getFullLocale(locale);

    const navEndpoint = getNavigationDataEndpoint(
      clientId,
      detectedDevice === 'desktop' ? 'web' : 'mob',
      locale,
      true,
    );
    const uspEndpoint = getUspDataEndpoint(clientId, locale);
    const footerCmsEndpoint = getEndpoint(footer.apiEndpoints.bucket, 'footer', locale);

    const navCacheKey = `nav-data-${clientId}-${localeForCacheKey}-${
      detectedDevice === 'desktop' ? 'web' : 'mob'
    }-true`;
    const uspCacheKey = `usp-data-${clientId}-${localeForCacheKey}`;

    const result = await execParallel([
      cached(navCacheKey, () => headerDefaultFetcher(navEndpoint)),
      cached(uspCacheKey, () => headerDefaultFetcher(uspEndpoint)),
      cached(`footer-${clientId}-${localeForCacheKey}`, () => bucketFetcher(footerCmsEndpoint)()),
    ]);

    fallback = {
      [userAgentDeviceCacheKey]: detectedDevice,
      [navCacheKey]: result[0],
      [uspCacheKey]: result[1],
      [`footer-${locale}`]: result[2],
    };
  }

  const appProps = await App.getInitialProps(appContext);

  if (!ctx.res?.writableEnded) {
    // testing browser cache (caching is unset in development)
    // this header is overridable in the page
    ctx.res?.setHeader('Cache-Control', 'max-age=180');
  }

  return {
    ...appProps,
    fallback,
    device: detectedDevice,
    ...(!isBot && { geoCountryCode }),
    isBot,
    cookies,
  };
};

// Report web-vitals - see https://nextjs.org/docs/advanced-features/measuring-performance
export function reportWebVitals(metric: NextWebVitalsMetric) {
  sendWebVitals(metric);
}

// eslint-disable-next-line import/no-default-export
export default MyApp;
